<template>
  <div class="row">
    <div class="col-md-offset-1 col-md-10">
      <div class="card" v-loading="loading">
        <div class="card-content">
          <h4 class="purple text-center">{{ $t('marketingSubscriptions.details') }}</h4>

          <!-- Family: new (created with this subscription) or an existing one (level on top) -->
          <div class="row">
            <div class="col-md-10 col-md-offset-1">
              <div v-if="isEdit && family" class="sub-form-family">
                <i class="fa fa-clone"></i>
                {{ $t('marketingSubscriptions.levelOf', { level: level, family: family.name }) }}
              </div>
              <div v-else class="sub-form-family sub-form-family--choose">
                <div class="form-group">
                  <label>{{ $t('marketingSubscriptions.family') }}*</label>
                  <div class="sub-form-modes">
                    <label class="sub-form-mode">
                      <input type="radio" value="new" v-model="form.family_mode"> {{ $t('marketingSubscriptions.familyNew') }}
                    </label>
                    <label class="sub-form-mode" :class="{ 'is-disabled': !families.length }">
                      <input type="radio" value="existing" v-model="form.family_mode" :disabled="!families.length"> {{ $t('marketingSubscriptions.familyExisting') }}
                    </label>
                  </div>
                </div>

                <div v-if="form.family_mode === 'new'" class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ $t('marketingSubscriptions.familyName') }}</label>
                      <input type="text" class="form-control" maxlength="255" v-model="form.family_name" :placeholder="form.name || $t('marketingSubscriptions.familyNameHint')">
                      <small class="text-muted">{{ $t('marketingSubscriptions.familyNameHint') }}</small>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <!-- One select: the company's payment groups of the supported processors (euPlatesc only for now) -->
                    <div class="form-group">
                      <label>{{ $t('marketingSubscriptions.paymentProcessor') }}*</label>
                      <el-select v-model="form.payment_group_key" class="w-100" :no-data-text="$t('marketingSubscriptions.noPaymentAccounts')">
                        <el-option v-for="group in paymentGroups" :key="group.group_key" :label="paymentGroupLabel(group)" :value="group.group_key"></el-option>
                      </el-select>
                      <small v-if="!paymentGroups.length" class="text-danger">{{ $t('marketingSubscriptions.noPaymentAccounts') }}</small>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <small class="text-danger" v-if="formErrors.payment">{{ $t('marketingSubscriptions.paymentRequired') }}</small>
                  </div>
                </div>

                <div v-else class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <el-select v-model="form.subscription_family_id" class="w-100" :placeholder="$t('marketingSubscriptions.selectFamily')">
                        <el-option v-for="item in families" :key="item.id" :label="item.name" :value="item.id"></el-option>
                      </el-select>
                      <small class="text-danger" v-if="formErrors.family">{{ $t('marketingSubscriptions.familyRequired') }}</small>
                    </div>
                  </div>
                  <div class="col-md-6" v-if="selectedFamily && selectedFamily.top_level">
                    <p class="sub-form-hint">
                      {{ $t('marketingSubscriptions.willBeLevel', { level: selectedFamily.next_level, name: selectedFamily.top_level.name, price: selectedFamily.top_level.price + ' ' + currency }) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>

          <div class="row">
            <!-- Main: name, price, description -->
            <div class="col-md-7 col-md-offset-1">
              <div class="row">
                <div class="col-md-7">
                  <div class="form-group">
                    <label>{{ $t('marketingSubscriptions.name') }}*</label>
                    <input type="text"
                           class="form-control"
                           :placeholder="$t('marketingSubscriptions.name')"
                           :maxlength="NAME_MAX"
                           v-model="form.name">
                    <small class="text-danger" v-if="formErrors.name">{{ $t('marketingSubscriptions.nameRequired') }}</small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>{{ $t('marketingSubscriptions.price') }}* <small class="text-muted">({{ currency }})</small></label>
                    <input type="number"
                           :min="minPrice"
                           step="0.01"
                           class="form-control"
                           :class="{ 'sub-price--invalid': priceOrderError }"
                           v-model="form.price">
                    <!-- shown while typing: a level must cost more than the one below (and less than the one above) -->
                    <small class="text-danger" v-if="priceOrderError">{{ priceOrderError }}</small>
                    <small class="text-danger" v-else-if="formErrors.price">{{ formErrors.price }}</small>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="form-group">
                    <label>{{ $t('marketingSubscriptions.active') }}</label>
                    <br/>
                    <p-switch v-model="form.active" type="success"></p-switch>
                  </div>
                </div>
              </div>
              <div v-if="levelBelow || levelAbove" class="sub-form-hint sub-form-hint--price">
                <div v-if="levelBelow">
                  {{ $t('marketingSubscriptions.priceHintBelow', { name: levelBelow.name, price: levelBelow.price + ' ' + currency, diff: upgradeDiffText }) }}
                </div>
                <div v-if="levelAbove">
                  {{ $t('marketingSubscriptions.priceHintAbove', { name: levelAbove.name, price: levelAbove.price + ' ' + currency }) }}
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('marketingSubscriptions.description') }}</label>
                <wysiwyg v-if="loaded" v-model="form.description" :options="editorOptions" class="form-control subscription-editor" />
              </div>
            </div>

            <!-- Side: image -->
            <div class="col-md-3">
              <div class="form-group">
                <label>{{ $t('marketingSubscriptions.image') }} <small class="text-muted">({{ $t('marketingSubscriptions.imageHint') }})</small></label>
                <el-upload
                  action=""
                  list-type="picture-card"
                  accept=".jpg,.jpeg,.png"
                  :file-list="form.imagelist"
                  :limit="1"
                  :auto-upload="false"
                  :on-change="handleImageChange"
                  :on-remove="handleImageRemove"
                  :on-exceed="handleExceed">
                  <img src="/static/img/icons/icon-plus-primary.png">
                </el-upload>
              </div>
            </div>
          </div>
          <hr>

          <div class="row">
            <div class="col-md-10 col-md-offset-1">
              <!-- Inherited from the lower levels (read only), grouped per level -->
              <div class="form-group">
                <label>{{ $t('marketingSubscriptions.inheritedCoupons') }}</label>
                <template v-if="inheritedGroups.length">
                  <div v-for="group in inheritedGroups" :key="'inhg' + group.level" class="sub-inherited-group">
                    <span class="sub-inherited-group__level">{{ $t('marketingSubscriptions.inheritedFromLevel', { level: group.level, name: group.name }) }}:</span>
                    <span v-for="coupon in group.coupons" :key="'inh' + group.level + '-' + coupon.id" class="sub-badge sub-badge--inherited">{{ coupon.name }}</span>
                    <span v-if="!group.coupons.length" class="text-muted">{{ $t('marketingSubscriptions.noneAssigned') }}</span>
                  </div>
                </template>
                <p v-else class="text-muted">{{ $t('marketingSubscriptions.noInherited') }}</p>
              </div>

              <!-- Own coupons: searchable multiple picker (same look as location > delivery method > Tip livrare) -->
              <div class="form-group">
                <div class="sub-own-head">
                  <label>
                    {{ $t('marketingSubscriptions.ownCouponsTitle') }}
                    <span class="subscription-selected-count" v-if="form.coupon_ids.length">{{ $t('marketingSubscriptions.selectedCount', { count: form.coupon_ids.length }) }}</span>
                  </label>
                  <button class="btn btn-sm btn-purple" @click.prevent="openCouponModal">
                    <i class="fa fa-plus"></i> {{ $t('marketingSubscriptions.newCoupon') }}
                  </button>
                </div>
                <small class="text-muted" style="display:block; margin-bottom:8px;">{{ $t('marketingSubscriptions.couponsHint') }}</small>
                <p v-if="!coupons.length" class="text-muted">{{ $t('marketingSubscriptions.noCoupons') }}</p>
                <template v-else>
                  <input type="text" class="form-control subscription-coupon-search" v-model="couponSearch" :placeholder="$t('marketingSubscriptions.searchCoupons')">
                  <div class="subscription-coupon-list">
                    <div v-for="coupon in filteredCoupons" :key="'coupon' + coupon.id"
                         class="subscription-coupon-row"
                         :class="{ 'is-checked': isCouponChecked(coupon.id), 'is-inherited': inheritedById[coupon.id] }"
                         @click="toggleCoupon(coupon.id)">
                      <input type="checkbox" :checked="isCouponChecked(coupon.id) || !!inheritedById[coupon.id]" :disabled="!!inheritedById[coupon.id]" style="margin:0; pointer-events:none;">
                      <span style="flex:1;">
                        <strong>{{ coupon.name }}</strong>
                        <small class="text-muted"> — {{ couponDiscountText(coupon) }}</small>
                        <span v-if="!coupon.active" class="subscription-coupon-inactive">{{ $t('marketingSubscriptions.inactive') }}</span>
                        <span v-if="inheritedById[coupon.id]" class="subscription-coupon-inherited">
                          {{ $t('marketingSubscriptions.inheritedIncluded', { level: inheritedById[coupon.id].level, name: inheritedById[coupon.id].name }) }}
                        </span>
                      </span>
                    </div>
                    <p v-if="!filteredCoupons.length" class="text-muted" style="padding:10px; margin:0;">{{ $t('marketingSubscriptions.noCouponsFound') }}</p>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="text-center mar-to-10">
            <button class="btn btn-purple-light btn-fill btn-wd" :disabled="saving" @click.prevent="save()">{{ $t('general.save') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- New subscription coupon without leaving the subscription -->
    <vmodal name="subscriptionCouponModal"
            class="subscriptionCouponModal"
            width="95%"
            :maxWidth="1100"
            :minWidth="300"
            height="auto"
            :scrollable="true"
            :adaptive="true"
            :clickToClose="false"
            @opened="onCouponModalOpened">
      <div class="sub-coupon-modal__header">
        <h4>{{ $t('marketingSubscriptions.newCouponTitle') }}</h4>
        <a class="sub-coupon-modal__close" @click="$modal.hide('subscriptionCouponModal')"><i class="fa fa-times"></i></a>
      </div>
      <div class="sub-coupon-modal__body">
        <coupon-form v-if="couponModalOpen" embedded force-subscription @saved="onCouponCreated"></coupon-form>
      </div>
    </vmodal>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Upload, Select, Option} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import 'vue-wysiwyg/dist/vueWysiwyg.css'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import vmodal from 'vue-js-modal'
  import CouponForm from '../DiscountCoupons/CouponForm.vue'

  Vue.use(Upload)
  Vue.use(Select)
  Vue.use(Option)
  Vue.use(vmodal, { componentName: 'vmodal' })

  // Must match the API (SubscriptionController / SubscriptionFamily::GATEWAYS:
  // /subscription/payment-groups only returns groups of those processors).
  const NAME_MAX = 255
  const MAX_IMAGE_MB = 5
  const GATEWAY_LABELS = {
    euplatesc: 'euPlatesc'
  }

  export default {
    components: {
      PSwitch,
      CouponForm
    },
    props: {
      route: {
        type: Object,
        default: () => ({})
      }
    },
    data () {
      return {
        NAME_MAX,
        // No inline images (they'd be stored as base64 inside the description).
        // The editor options are merged shallowly, so the global hidden modules are repeated here.
        editorOptions: {
          hideModules: { code: true, table: true, image: true }
        },
        loading: false,
        loaded: false,
        saving: false,
        currency: '',
        form: {
          family_mode: 'new',
          family_name: '',
          payment_group_key: '',
          subscription_family_id: null,
          name: '',
          price: '',
          active: true,
          description: '',
          imagelist: [],
          coupon_ids: []
        },
        formErrors: {},
        // edit mode (from the API)
        level: null,
        family: null,
        editLevelBelow: null,
        editLevelAbove: null,
        lowerLevels: [],
        // lookups
        families: [],
        paymentGroups: [],
        coupons: [],
        couponSearch: '',
        couponModalOpen: false
      }
    },
    computed: {
      subscriptionId () {
        return this.route.params && this.route.params.id
      },
      isEdit () {
        return !!this.subscriptionId
      },
      selectedPaymentGroup () {
        return this.paymentGroups.find(group => group.group_key === this.form.payment_group_key) || null
      },
      selectedFamily () {
        return this.families.find(item => item.id === this.form.subscription_family_id) || null
      },
      levelBelow () {
        if (this.isEdit) {
          return this.editLevelBelow
        }
        return this.form.family_mode === 'existing' && this.selectedFamily ? this.selectedFamily.top_level : null
      },
      levelAbove () {
        return this.isEdit ? this.editLevelAbove : null
      },
      // live check of the price against the neighbour levels ('' = ok / not typed yet)
      priceOrderError () {
        const price = parseFloat(this.form.price)
        if (this.form.price === '' || this.form.price === null || isNaN(price)) {
          return ''
        }
        if (this.levelBelow && price <= parseFloat(this.levelBelow.price)) {
          return this.$t('marketingSubscriptions.priceTooLow', { price: this.levelBelow.price + ' ' + this.currency })
        }
        if (this.levelAbove && price >= parseFloat(this.levelAbove.price)) {
          return this.$t('marketingSubscriptions.priceTooHigh', { price: this.levelAbove.price + ' ' + this.currency })
        }
        return ''
      },
      minPrice () {
        return this.levelBelow ? (parseFloat(this.levelBelow.price) + 0.01).toFixed(2) : 0
      },
      upgradeDiffText () {
        const price = parseFloat(this.form.price)
        if (!this.levelBelow || isNaN(price)) {
          return '-'
        }
        return (price - parseFloat(this.levelBelow.price)).toFixed(2) + ' ' + this.currency
      },
      // Coupons the level gets from the lower levels, grouped per level (lowest first)
      inheritedGroups () {
        // edit: the levels below this one; add on top of a family: all its levels
        const levels = this.isEdit
          ? this.lowerLevels
          : (this.form.family_mode === 'existing' && this.selectedFamily ? this.selectedFamily.levels || [] : [])
        return levels.map(level => ({
          level: level.level,
          name: level.name,
          coupons: level.coupons || []
        }))
      },
      inheritedById () {
        const map = {}
        this.inheritedGroups.forEach(group => {
          group.coupons.forEach(coupon => {
            if (!map[coupon.id]) {
              map[coupon.id] = { level: group.level, name: group.name }
            }
          })
        })
        return map
      },
      filteredCoupons () {
        const q = (this.couponSearch || '').trim().toLowerCase()
        if (!q) {
          return this.coupons
        }
        return this.coupons.filter(coupon =>
          (coupon.name || '').toLowerCase().indexOf(q) !== -1 ||
          (coupon.coupon_code || '').toLowerCase().indexOf(q) !== -1
        )
      }
    },
    watch: {
      paymentGroups (groups) {
        // Preselect the only processor account.
        if (!this.form.payment_group_key && groups.length === 1) {
          this.form.payment_group_key = groups[0].group_key
        }
      }
    },
    created () {
      this.currency = this.getCurrencySymbol(this.$session.get('selectedCompanyData').currency_iso)
      this.getCoupons()
      if (this.isEdit) {
        this.getSubscription()
      } else {
        this.getFamilies()
        this.getPaymentGroups()
        this.loaded = true
      }
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      getCoupons (callback) {
        this.apiRequest('/subscription/coupons', 'GET', this.requestData(), (response) => {
          if (response.status === true) {
            this.coupons = response.data || []
          }
          if (callback) {
            callback()
          }
        })
      },
      /* ---------------- New coupon popup ---------------- */
      openCouponModal () {
        this.couponModalOpen = true
        this.$modal.show('subscriptionCouponModal')
      },
      onCouponModalOpened () {
        // `.main-panel` / the route modal have transforms: re-parent to <body> so it stays on top
        const el = document.querySelector('.subscriptionCouponModal.vm--container')
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el)
        }
      },
      onCouponCreated (coupon) {
        this.$modal.hide('subscriptionCouponModal')
        this.couponModalOpen = false
        this.getCoupons(() => {
          if (coupon && coupon.id && this.form.coupon_ids.indexOf(coupon.id) === -1) {
            this.form.coupon_ids.push(coupon.id)
          }
        })
      },
      getFamilies () {
        this.apiRequest('/subscription/families', 'GET', this.requestData(), (response) => {
          if (response.status !== true) {
            return
          }
          this.families = response.data || []
          // "Add a level on top of ..." opens the form with the family preselected.
          const familyId = parseInt(this.route.query && this.route.query.family_id, 10)
          if (familyId && this.families.find(item => item.id === familyId)) {
            this.form.family_mode = 'existing'
            this.form.subscription_family_id = familyId
          }
        })
      },
      getPaymentGroups () {
        this.apiRequest('/subscription/payment-groups', 'GET', this.requestData(), (response) => {
          if (response.status === true) {
            this.paymentGroups = response.data || []
          }
        })
      },
      getSubscription () {
        this.loading = true
        this.apiRequest('/subscription/' + this.subscriptionId, 'GET', this.requestData(), (response) => {
          this.loading = false
          if (!(response.status === true && response.data)) {
            return
          }
          const data = response.data
          this.level = data.level
          this.family = data.family
          this.editLevelBelow = data.level_below
          this.editLevelAbove = data.level_above
          this.lowerLevels = data.lower_levels || []
          this.form = {
            ...this.form,
            name: data.name || '',
            price: data.price,
            active: !!data.active,
            description: data.description || '',
            imagelist: data.image_filename
              ? [{ url: data.image_url, name: data.image_filename, filename: data.image_filename }]
              : [],
            coupon_ids: Array.isArray(data.coupon_ids) ? data.coupon_ids.slice() : []
          }
          this.loaded = true
        })
      },
      paymentGroupLabel (group) {
        return (group.group_name || group.group_key) + ' · ' + (GATEWAY_LABELS[group.gateway] || group.gateway)
      },
      /* ---------------- Coupons picker ---------------- */
      isCouponChecked (id) {
        return this.form.coupon_ids.indexOf(id) !== -1
      },
      toggleCoupon (id) {
        if (this.inheritedById[id] && this.form.coupon_ids.indexOf(id) === -1) {
          return
        }
        const i = this.form.coupon_ids.indexOf(id)
        if (i === -1) {
          this.form.coupon_ids.push(id)
        } else {
          this.form.coupon_ids.splice(i, 1)
        }
      },
      couponDiscountText (coupon) {
        if (coupon.type === 'FREE_PRODUCT') {
          return this.$t('marketingSubscriptions.freeProduct')
        }
        return (coupon.discount || 0) + ' ' + (coupon.type === 'FIXED' ? this.currency : '%')
      },
      /* ---------------- Image ---------------- */
      handleImageChange (file, fileList) {
        if (file.size && (file.size / 1024 / 1024) > MAX_IMAGE_MB) {
          swal({
            toast: true,
            type: 'warning',
            text: this.$t('validations.pictureSizeExceed'),
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          }).catch(() => {})
          this.form.imagelist = fileList.filter(f => f.uid !== file.uid).slice(-1)
          return
        }
        this.form.imagelist = fileList.slice(-1)
      },
      handleImageRemove () {
        this.form.imagelist = []
      },
      handleExceed () {
        swal('', this.$t('validations.pictureSizeExceed'), 'warning').catch(() => {})
      },
      async collectImage () {
        const f = this.form.imagelist && this.form.imagelist[0]
        if (!f) {
          return ''
        }
        if (f.raw) {
          return this.getBase64(f.raw)
        }
        return f.filename || ''
      },
      /* ---------------- Save ---------------- */
      validate () {
        this.formErrors = {}
        if (!(this.form.name || '').trim()) {
          this.$set(this.formErrors, 'name', true)
        }
        const price = parseFloat(this.form.price)
        if (this.form.price === '' || this.form.price === null || isNaN(price) || price < 0) {
          this.$set(this.formErrors, 'price', this.$t('marketingSubscriptions.priceRequired'))
        } else if (this.priceOrderError) {
          this.$set(this.formErrors, 'price', this.priceOrderError)
        }
        if (!this.isEdit) {
          if (this.form.family_mode === 'new' && !this.selectedPaymentGroup) {
            this.$set(this.formErrors, 'payment', true)
          }
          if (this.form.family_mode === 'existing' && !this.form.subscription_family_id) {
            this.$set(this.formErrors, 'family', true)
          }
        }
        return !Object.keys(this.formErrors).length
      },
      async save () {
        if (!this.validate()) {
          return
        }

        this.saving = true

        let image
        try {
          image = await this.collectImage()
        } catch (e) {
          this.saving = false
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          return
        }

        const data = {
          ...this.requestData(),
          name: this.form.name,
          price: this.form.price,
          active: this.form.active ? 1 : 0,
          description: this.form.description,
          image: image,
          coupon_ids: this.form.coupon_ids
        }
        if (!this.isEdit) {
          data.family_mode = this.form.family_mode
          if (this.form.family_mode === 'new') {
            data.family_name = this.form.family_name
            data.payment_gateway = this.selectedPaymentGroup.gateway
            data.payment_group_key = this.selectedPaymentGroup.group_key
          } else {
            data.subscription_family_id = this.form.subscription_family_id
          }
        }
        const url = this.isEdit ? '/subscription/' + this.subscriptionId : '/subscription'
        const method = this.isEdit ? 'PUT' : 'POST'

        this.apiRequest(url, method, data, (response) => {
          this.saving = false
          if (response.status === true) {
            swal({
              title: '',
              text: this.$t('marketingSubscriptions.saved'),
              type: 'success',
              showConfirmButton: false,
              timer: 1500
            }).catch(() => {})
            this.goBack()
            return
          }

          const errors = response.errors || {}
          const messages = Object.keys(errors).map(key => Array.isArray(errors[key]) ? errors[key][0] : errors[key])
          swal('', messages.join(' ') || this.$t('validations.wentWrong'), 'error').catch(() => {})
        })
      },
      goBack () {
        this.$bus.$emit('refresh')
        this.$bus.$emit('close-modal')
      }
    }
  }
</script>
<style>
  .subscription-editor {
    min-height: 260px;
    padding: 0;
  }
  .sub-form-family {
    font-size: 14px;
    color: #555;
  }
  .sub-form-family .fa {
    color: #7e57c2;
    margin-right: 6px;
  }
  .sub-form-modes {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }
  .sub-form-mode {
    font-weight: normal;
    cursor: pointer;
    text-transform: none;
  }
  .sub-form-mode.is-disabled {
    color: #bbb;
    cursor: not-allowed;
  }
  .sub-price--invalid {
    border-color: #e53935 !important;
  }
  .sub-form-hint {
    font-size: 13px;
    color: #777;
    margin: 6px 0 0;
  }
  .sub-form-hint--price {
    margin: -6px 0 12px;
  }
  .sub-form-family .w-100 {
    width: 100%;
  }
  .sub-badge {
    display: inline-block;
    margin: 2px 4px 2px 0;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.4;
  }
  .sub-badge--inherited {
    background: #fafafa;
    color: #777;
    border: 1px dashed #bbb;
  }
  .subscription-selected-count {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    background: #eef7ee;
    color: #2e7d32;
    border: 1px solid #c8e6c9;
  }
  .subscription-coupon-search {
    margin-bottom: 8px;
    max-width: 560px;
  }
  .subscription-coupon-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }
  .subscription-coupon-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
  }
  .subscription-coupon-row.is-checked {
    background: #eef7ee;
  }
  .subscription-coupon-row.is-inherited {
    background: #fafafa;
    cursor: default;
  }
  .subscription-coupon-inherited {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 11px;
    color: #777;
    border: 1px dashed #bbb;
  }
  .sub-inherited-group {
    margin-bottom: 4px;
  }
  .sub-inherited-group__level {
    font-size: 13px;
    color: #555;
    margin-right: 4px;
  }
  .sub-own-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .subscriptionCouponModal.vm--container {
    z-index: 100500;
  }
  .subscriptionCouponModal .vm--modal {
    top: 20px !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 auto !important;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
  }
  .sub-coupon-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 18px;
    border-bottom: 1px solid #ddd;
  }
  .sub-coupon-modal__header h4 {
    margin: 0;
  }
  .sub-coupon-modal__close {
    cursor: pointer;
    font-size: 18px;
    color: #777;
  }
  .sub-coupon-modal__body {
    padding: 10px 0;
  }
  .subscription-coupon-inactive {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 11px;
    background: #f5f5f5;
    color: #999;
    border: 1px solid #e0e0e0;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/Subscriptions/SubscriptionForm.vue